import { useEffect, useMemo, useState } from 'react';
import { Alert, Button, InputNumber, Modal, Space, Typography, message } from 'antd';
import { useTranslation } from 'react-i18next';
import {
  seedSplitPassengerCounts,
  suggestSplitVehicleCount,
  validateSplitPassengerCounts,
  splitOversizedJob,
} from '@ubus/shared';
import type { Job } from '@ubus/shared';

const { Text } = Typography;

export interface JobSplitModalProps {
  open: boolean;
  job: Job | null;
  maxCapacity: number;
  onCancel: () => void;
  onSuccess: () => void;
}

export function JobSplitModal({
  open,
  job,
  maxCapacity,
  onCancel,
  onSuccess,
}: JobSplitModalProps) {
  const { t } = useTranslation();
  const suggested = useMemo(
    () =>
      job ? suggestSplitVehicleCount(job.passenger_count, maxCapacity) : 2,
    [job, maxCapacity]
  );
  const [vehicleCount, setVehicleCount] = useState(2);
  const [counts, setCounts] = useState<number[]>([1, 1]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !job) return;
    const n = suggestSplitVehicleCount(job.passenger_count, maxCapacity);
    setVehicleCount(n);
    setCounts(seedSplitPassengerCounts(job.passenger_count, maxCapacity, n));
  }, [open, job, maxCapacity]);

  const validation = job
    ? validateSplitPassengerCounts(job.passenger_count, maxCapacity, counts)
    : { ok: false as const, code: 'need_at_least_two' as const };

  const sum = counts.reduce((a, b) => a + b, 0);

  const applyVehicleCount = (n: number | null) => {
    if (!job || n == null || n < 2) return;
    setVehicleCount(n);
    setCounts(seedSplitPassengerCounts(job.passenger_count, maxCapacity, n));
  };

  const setCountAt = (index: number, value: number | null) => {
    if (value == null) return;
    setCounts((prev) => prev.map((c, i) => (i === index ? value : c)));
  };

  const handleOk = async () => {
    if (!job || !validation.ok) return;
    setSaving(true);
    try {
      const result = await splitOversizedJob(job.id, maxCapacity, counts);
      message.success(
        t('jobs.splitSuccess', { count: result.vehicle_count })
      );
      onSuccess();
    } catch (err) {
      console.error(err);
      const detail =
        err && typeof err === 'object' && 'message' in err
          ? String((err as { message?: string }).message ?? '')
          : '';
      message.error(detail ? `${t('jobs.splitFailed')}: ${detail}` : t('jobs.splitFailed'));
    } finally {
      setSaving(false);
    }
  };

  const errorHint = !validation.ok
    ? t(`jobs.splitError.${validation.code}`)
    : null;

  return (
    <Modal
      title={t('jobs.splitTitle')}
      open={open}
      onCancel={onCancel}
      onOk={() => void handleOk()}
      okText={t('jobs.splitConfirm')}
      cancelText={t('common.cancel')}
      confirmLoading={saving}
      okButtonProps={{ disabled: !validation.ok }}
      destroyOnClose
    >
      {job ? (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Text>
            {t('jobs.splitSummary', {
              title: job.title,
              passengers: job.passenger_count,
              capacity: maxCapacity,
              suggested,
            })}
          </Text>
          <div>
            <Text type="secondary">{t('jobs.splitVehicleCount')}</Text>
            <div>
              <InputNumber
                min={2}
                max={12}
                value={vehicleCount}
                onChange={applyVehicleCount}
                style={{ width: 120 }}
              />
            </div>
          </div>
          {counts.map((c, i) => (
            <div key={i}>
              <Text>
                {t('jobs.splitBusLabel', { index: i + 1 })}{' '}
                <Text type="secondary">
                  ({t('jobs.splitMaxHint', { capacity: maxCapacity })})
                </Text>
              </Text>
              <div>
                <InputNumber
                  min={1}
                  max={maxCapacity}
                  value={c}
                  onChange={(v) => setCountAt(i, v)}
                  style={{ width: 120 }}
                />
              </div>
            </div>
          ))}
          <Text>
            {t('jobs.splitTotal', {
              sum,
              total: job.passenger_count,
            })}
          </Text>
          {errorHint ? <Alert type="error" showIcon message={errorHint} /> : null}
        </Space>
      ) : null}
    </Modal>
  );
}

export default JobSplitModal;
